import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import Constants from "../../common/Constants";

interface ReservationRow {
  _id: string;
  threadId?: string;
  name?: string | null;
  phone?: string | null;
  place?: string | null;
  date?: string | null;
  time?: string | null;
  eventTitle?: string | null;
  status?: string;
  updatedAt?: string;
}

const Page = styled.div`
  flex: 3;
  padding: 2rem;
  background-color: #fafafa;
  min-height: 100vh;
`;

const Header = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #333;
`;

const Description = styled.p`
  color: #777;
  margin-bottom: 1.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Th = styled.th`
  text-align: left;
  padding: 0.85rem 1rem;
  background: #0b261c;
  color: #fff;
  font-size: 0.85rem;
`;

const Td = styled.td`
  padding: 0.85rem 1rem;
  border-top: 1px solid #eee;
  color: #222;
  font-size: 0.95rem;
`;

const Empty = styled.p`
  color: #666;
  background: #fff;
  padding: 1.5rem;
  border-radius: 8px;
`;

const ConfirmButton = styled.button`
  background: #0b261c;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionError = styled.p`
  color: #8a1f1f;
  margin-bottom: 1rem;
`;

function statusLabel(status?: string): string {
  if (status === "complete") return "Awaiting confirmation";
  if (status === "confirmed") return "Confirmed";
  if (status === "partial") return "Missing details";
  if (status === "cancelled") return "Cancelled";
  if (status === "invalid_date") return "Invalid date";
  return status || "Unknown";
}

function reference(id: string): string {
  const raw = id.replace(/[^a-zA-Z0-9]/g, "");
  const tail = (raw.slice(-6) || "000000").toUpperCase();
  return `CDL-${new Date().getFullYear()}-${tail}`;
}

const ReservationsScreen = () => {
  const [rows, setRows] = useState<ReservationRow[]>([]);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(
          `${Constants.serverlink}/reservations?owner=casino`
        );
        setRows(response.data?.reservations ?? []);
      } catch (err) {
        console.error(err);
        setError("Could not load reservations from the API.");
      }
    };
    void load();
  }, []);

  const confirmReservation = async (id: string) => {
    setConfirmingId(id);
    setActionError("");
    try {
      const response = await axios.post(
        `${Constants.serverlink}/reservations/${id}/confirm`
      );
      const updated = response.data;
      setRows((current) =>
        current.map((row) =>
          row._id === id
            ? { ...row, ...updated, status: updated?.status || "confirmed" }
            : row
        )
      );
    } catch (err) {
      console.error(err);
      setActionError("Could not confirm this reservation.");
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <Page>
      <Header>Reservations</Header>
      <Description>
        Booking requests from LAILA. A complete request is awaiting staff confirmation.
      </Description>
      {error ? <Empty>{error}</Empty> : null}
      {actionError ? <ActionError>{actionError}</ActionError> : null}
      {!error && rows.length === 0 ? (
        <Empty>No reservations yet. Complete a booking in the chat widget and refresh this page.</Empty>
      ) : null}
      {rows.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th>Reference</Th>
              <Th>Guest</Th>
              <Th>Phone</Th>
              <Th>Venue</Th>
              <Th>When</Th>
              <Th>Status</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row._id}>
                <Td>{reference(row._id)}</Td>
                <Td>{row.name || "—"}</Td>
                <Td>{row.phone || "—"}</Td>
                <Td>{row.eventTitle || row.place || "—"}</Td>
                <Td>
                  {[row.date, row.time].filter(Boolean).join(" ") || "—"}
                </Td>
                <Td>{statusLabel(row.status)}</Td>
                <Td>
                  {row.status === "complete" ? (
                    <ConfirmButton
                      type="button"
                      disabled={confirmingId === row._id}
                      onClick={() => void confirmReservation(row._id)}
                    >
                      {confirmingId === row._id ? "Confirming…" : "Confirm"}
                    </ConfirmButton>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
    </Page>
  );
};

export default ReservationsScreen;
